import React, { Fragment } from 'react';
import PropTypes from 'prop-types';

import MatrixHead from './MatrixHead';
import MatrixRow, { MATRIX_CELL } from './MatrixRow';
import {
  GroupCell,
  MatrixScroll,
  MatrixTable,
  RowError,
  TIER_OPTIONS_PORTAL_ID,
  TierOptionsPortal,
} from './styles';

/**
 * The messages to show under a row. A row names the accounts it stands for, and
 * the server keys refusals by account id, so the ALL row gathers them rather
 * than looking itself up under its own id — the literal 'all', which matches
 * nothing. Deduplicated, since one refusal usually lands on every account.
 */
const messagesFor = (row, errors) => {
  const ids = row.accountIds || [row.id];
  const messages = ids.flatMap((id) => errors[id] || []);

  return [...new Set(messages)];
};

/**
 * The accounts × permissions table, grouped by team. "Apply to All Accounts" is
 * not a branch: the hook hands over one group holding one row, so that frame
 * comes out of the same code path as the per-team one.
 *
 * Hand-rolled rather than KeyDS `Table` — see the note in ./styles.
 */
function PermissionsMatrix({ columns, errors, groups, onCellChange }) {
  return (
    <>
      {/* Outside MatrixScroll on purpose — see the note on TierOptionsPortal. */}
      <TierOptionsPortal id={TIER_OPTIONS_PORTAL_ID} />

      <MatrixScroll>
        <MatrixTable>
          <MatrixHead columns={columns} />

          {/* One tbody per team, not one for the whole table. `scope="rowgroup"`
              only means anything inside the row group it heads, and without the
              split every team header would claim every row in the table —
              including the other teams'. */}
          {groups.map((group) => (
            <tbody key={group.id}>
              <tr>
                <GroupCell colSpan={columns.length} scope="rowgroup">
                  {group.name}
                </GroupCell>
              </tr>

              {group.rows.map((row, index) => {
                const messages = messagesFor(row, errors);

                return (
                  <Fragment key={row.id}>
                    <MatrixRow
                      cells={row.cells}
                      name={row.name}
                      onCellChange={onCellChange}
                      rowId={row.id}
                      stripe={index % 2 === 1}
                    />

                    {messages.length > 0 && (
                      <tr>
                        <td colSpan={columns.length}>
                          <RowError role="alert">{messages.join(' ')}</RowError>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          ))}
        </MatrixTable>
      </MatrixScroll>
    </>
  );
}

PermissionsMatrix.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  /** Server refusals keyed by row id. */
  errors: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)),
  groups: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      rows: PropTypes.arrayOf(
        PropTypes.shape({
          /** The accounts this row stands for; defaults to its own id. */
          accountIds: PropTypes.arrayOf(PropTypes.string),
          cells: PropTypes.arrayOf(MATRIX_CELL),
          id: PropTypes.string,
          name: PropTypes.string,
        }),
      ),
    }),
  ).isRequired,
  onCellChange: PropTypes.func.isRequired,
};

PermissionsMatrix.defaultProps = {
  errors: {},
};

export default PermissionsMatrix;
